import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import { allowedToView } from "../../utils/PermissionUtils.js";

const DebtView = () => {
    const { id } = useParams();
    const [debt, setDebt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const token = localStorage.getItem("access_token");
    const navigate = useNavigate();   
    const userRole = localStorage.getItem("userRole");
        const canDelete = (userRole) => {
            return (allowedToView(userRole, ['admin', 'superadmin']));
        };

    useEffect(() => {
        const fetchDebt = async () => {
            try {
                const response = await fetch(`${API_URL}/debts/${id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setDebt(data);
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    const errorData = await response.json();
                    setError(errorData || {});
                    setNotification({ message: errorData.message || "An error occurred.", type: "danger" });
                }

            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchDebt();
        }
    }, [id, token, navigate]);

    const handleDeleteDebt = (debtId) => {
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteDebt = async () => {
        setIsDeleting(true);
        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(`${API_URL}/debts/${debt.id}`, {
                method: 'DELETE',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setNotification({ type: "success", message: 'Debt deleted successfully' });

                setTimeout(() => {
                    navigate('/dashboard/debts')
                }, 5000);

            } else if (response.status === 401) {
                navigate("/logout");
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: 'Debt not found.' });
            } else if (response.status === 400 || response.status === 422) {
                const errorData = await response.json();
                setNotification({
                    type: "danger",
                    message: errorData.message || "Failed to delete debt",
                    isVisible: true,
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                throw new Error("Failed to delete debt.");
            }
        } catch (error) {
            setNotification({ message: "An error occurred while deleting the debt.", type: "danger" });
            console.error("Error:", error);
        } finally {
            setIsDeleting(false);
            setIsConfirmDialogOpen(false);
        }
    };

    const cancelDeleteDebt = () => {
        setIsConfirmDialogOpen(false);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) return <div className="container"><p className="has-text-centered py-6">Loading debt details...</p></div>;
    if (error) return <div className="container"><p className="has-text-centered has-text-danger py-6">{error}</p></div>;

    return (
        <div className="container">
            <div className="section">
                <div className="level">
                    <div className="level-left">
                        <h2 className="title is-3">Debt Details</h2>
                    </div>
                    <div className="level-right">
                        <Link className="button is-light" to="/dashboard/debts">
                            <span className="icon">
                                <i className="fas fa-arrow-left"></i>
                            </span>
                            <span>Back to Debts</span>
                        </Link>
                    </div>
                </div>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {debt && (
                    <div className="box">
                        {/* Farmer Information Card - Now full width */}
                        <div className="card mb-5">
                            <div className="card-content">
                                <div className="media">
                                    <div className="media-content">
                                        <p className="title is-4"><Link to={`/dashboard/farmers/${debt.farmer.id}/details`} className="no_deco_link">
                                            {debt.farmer.first_name} {debt.farmer.middle_name} {debt.farmer.last_name}
                                        </Link></p>
                                    </div>
                                </div>
                                <div className="content">
                                    <div className="field is-horizontal">
                                        <div className="field-label is-normal">
                                            <label className="label">Phone</label>
                                        </div>
                                        <div className="field-body">
                                            <div className="field">
                                                <p className="control">{debt.farmer.phone_number}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="field is-horizontal">
                                        <div className="field-label is-normal">
                                            <label className="label">ID Number</label>
                                        </div>
                                        <div className="field-body">
                                            <div className="field">
                                                <p className="control">{debt.farmer.id_number}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Confirmation Dialog */}
                        <ConfirmDialog
                            isOpen={isConfirmDialogOpen}
                            theme="is-danger"
                            title="Delete Confirmation"
                            message={`Are you sure you want to delete debt of ${debt.amount} for ${[debt?.farmer?.first_name, debt?.farmer?.middle_name, debt?.farmer?.last_name]
                                .filter(Boolean)
                                .join(' ')
                                } (${debt?.farmer?.farmer_number}) taken on ${debt?.date_taken}?`}
                            onConfirm={confirmDeleteDebt}
                            onCancel={cancelDeleteDebt}
                            isLoading={isDeleting}
                        />

                        {/* Debt Information Card  */}
                        <div className="card">
                            <div className="card-content">
                                <h3 className="title is-5">Debt Information</h3>
                                <div className="columns">
                                    <div className="column is-three-quarters">
                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Product</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">{debt.product.name}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Items</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">{debt.no_of_items}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Price Per Item</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">KES {debt.price_per_item}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Amount</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control has-text-weight-semibold">KES {debt.amount}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Deducted</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">KES {debt.deducted_amount}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Balance</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className={`control has-text-weight-bold ${parseFloat(debt.balance_due) > 0 ? 'has-text-danger' : 'has-text-success'}`}>
                                                        KES {debt.balance_due}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Status</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">
                                                        <span className={`tag is-medium ${debt.status === 'Cleared' ? 'is-success' : 'is-warning'}`}>
                                                            {debt.status}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <hr className="my-4" />

                                        <div className="field is-horizontal">
                                            <div className="field-label is-normal">
                                                <label className="label">Date Taken</label>
                                            </div>
                                            <div className="field-body">
                                                <div className="field">
                                                    <p className="control">{new Date(debt.date_taken).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {debt.cleared_at && (
                                            <div className="field is-horizontal">
                                                <div className="field-label is-normal">
                                                    <label className="label">Cleared At</label>
                                                </div>
                                                <div className="field-body">
                                                    <div className="field">
                                                        <p className="control">{new Date(debt.cleared_at).toLocaleString()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Right column for delete button */}
                                    <div className="column has-text-right">
                                        <div className="is-flex is-justify-content-flex-end is-align-items-flex-end" style={{ height: '100%' }}>
                                            
                                        {canDelete(userRole) && (
                                            <button
                                                className={`button is-danger is-outlined ${isDeleting ? 'is-loading' : ''}`}
                                                onClick={() => handleDeleteDebt(debt.id)}
                                                disabled={isDeleting}
                                            >
                                                <span className="icon">
                                                    <i className="fas fa-trash"></i>
                                                </span>
                                                <span>Delete</span>
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {debt.deductions && debt.deductions.length > 0 && (
                            <div className="card mt-5">
                                <div className="card-header">
                                    <p className="card-header-title">Deduction History</p>
                                </div>
                                <div className="card-content">
                                    <table className="table is-fullwidth is-striped is-hoverable">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Deducted Amount (KES)</th>
                                                <th>Remaining Balance (KES)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {debt.deductions.map((deduction, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        {new Date(deduction.created_at).toLocaleDateString(undefined, {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                        })}
                                                    </td>
                                                    <td>{parseFloat(deduction.deducted_amount).toLocaleString()}</td>
                                                    <td>{parseFloat(deduction.remaining_balance).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}


                    </div>
                )}
            </div>
        </div>
    );
};

export default DebtView;